import React from 'react';
import {
  Box,
  Flex,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
} from '@chakra-ui/react';

import xhr from 'xhr';

const BATCH_SIZE = 50;

const normalize = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/** Solo lectura: muestra a quién le llega la campaña con los filtros actuales. */
const RecipientsModal = ({ isOpen, onClose, payload }) => {
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [visibleCount, setVisibleCount] = React.useState(BATCH_SIZE);
  const bodyRef = React.useRef(null);
  const sentinelRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen || !payload) return;

    let cancelled = false;
    setIsLoading(true);
    setError(false);
    setSearch('');
    setVisibleCount(BATCH_SIZE);

    xhr
      .post('/email-campaigns/filter/students', { data: { ...payload, detail: true } })
      .then((response) => {
        if (!cancelled) setRows(response.data.data);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, payload]);

  const filteredRows = React.useMemo(() => {
    const term = normalize(search.trim());
    if (!term) return rows;
    return rows.filter((row) =>
      [row.id, row.name, row.identity, row.email].some((value) => normalize(value).includes(term))
    );
  }, [rows, search]);

  const visibleRows = filteredRows.slice(0, visibleCount);
  const hasMore = visibleCount < filteredRows.length;

  // Scroll infinito: los datos ya están completos; se van mostrando de a BATCH_SIZE al llegar al final.
  React.useEffect(() => {
    if (!hasMore || !sentinelRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((count) => count + BATCH_SIZE);
      },
      { root: bodyRef.current, rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, visibleCount, filteredRows]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Destinatarios de la campaña</ModalHeader>
        <ModalCloseButton />
        <ModalBody paddingBottom="20px" ref={bodyRef}>
          {isLoading ? (
            <Flex justifyContent="center" paddingY="40px">
              <Spinner />
            </Flex>
          ) : error ? (
            <Text color="red.500">No se pudo cargar el listado de destinatarios.</Text>
          ) : (
            <>
              <Text fontSize="sm" color="gray.600">
                <Text as="span" fontWeight="bold">
                  {rows.length}
                </Text>{' '}
                correos se enviarán con estos filtros (uno por matrícula, así que un estudiante con varias matrículas aparece y recibe varias veces).
              </Text>

              <Input
                marginTop="10px"
                size="sm"
                maxW="360px"
                placeholder="Buscar por código, nombre, documento o correo"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setVisibleCount(BATCH_SIZE);
                }}
              />

              <TableContainer marginTop="12px" borderWidth="1px" borderColor="gray.100" borderRadius="md">
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Código</Th>
                      <Th>Nombre</Th>
                      <Th>Documento</Th>
                      <Th>Correo</Th>
                      <Th>Programa</Th>
                      <Th>Correos que recibe</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visibleRows.map((row, index) => (
                      <Tr key={`${row.id}-${index}`}>
                        <Td>{row.id}</Td>
                        <Td>{row.name || '—'}</Td>
                        <Td>{row.identity || '—'}</Td>
                        <Td>{row.email || '—'}</Td>
                        <Td minW="220px" maxW="320px" whiteSpace="normal">
                          <Text noOfLines={2} title={row.programs}>
                            {row.programs || '—'}
                          </Text>
                        </Td>
                        <Td>{row.enrollments}</Td>
                      </Tr>
                    ))}
                    {!visibleRows.length && (
                      <Tr>
                        <Td colSpan={6} textAlign="center" color="gray.500" paddingY="20px">
                          Sin resultados
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>

              {hasMore && (
                <Flex ref={sentinelRef} justifyContent="center" paddingY="12px">
                  <Spinner size="sm" />
                </Flex>
              )}

              {filteredRows.length > BATCH_SIZE && (
                <Box fontSize="xs" color="gray.500" textAlign="right" marginTop="6px">
                  Mostrando {visibleRows.length} de {filteredRows.length}
                </Box>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RecipientsModal;
